import { HttpService } from '@maia/httpClient';
import { BaseService } from '@maia/feature-utils';
import { ConfigService } from '@maia/config';
import {
  AddressDto,
  BillingCycleUpdateDto,
  IAddressEntity,
  IAttachmentEntity,
  IbanUpdateDto,
  ICustomerEntity,
} from '../../shared';
import { CustomerCreateDto, CustomerDto } from '../../dtos/customer.dto';
import Cookies from 'js-cookie';
import { AddressPostDto } from '../../dtos/common/address.dto';

type ImpersonateResult =
  | { type: 'ok'; data: string }
  | { type: 'error'; error: string };

type ImpersonateData = string | { url?: string; data?: string };

type ImpersonateResponse =
  | { success: true; error: null; response: ImpersonateData }
  | { success: false; error: string; response: null };

// Private method Props types for consistent destructuring pattern
type AuthFetchProps = {
  url: string;
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: Record<string, unknown>;
  accessToken: string;
};

type FetchWithRetryProps = {
  url: string;
  accessToken: string;
};

type UpdateIbanProps = {
  customerGuid: string;
  body: ConstructorParameters<typeof IbanUpdateDto>[number];
};

type UpdateBillingCycleProps = {
  customerGuid: string;
  body: ConstructorParameters<typeof BillingCycleUpdateDto>[number];
};

type UpdateAddressProps = {
  customerGuid: string;
  body: {
    address: Omit<AddressDto, 'guid'>;
    effectiveDate: string;
  };
};

type UpdatePersonalDataPayload = {
  initials: string;
  first_name: string;
  last_name: string;
};

type UpdatePersonalDataParams = {
  customerGuid: string;
  body: {
    initials: CustomerDto['consumer']['initials'];
    firstname: CustomerDto['consumer']['firstName'];
    lastname: CustomerDto['consumer']['lastName'];
  };
};

type RemoveProps = {
  guid: string;
  email: string;
};

type UpdateDetailsProps = {
  guid: string;
  old_email: string;
  email: string;
  phone?: string;
};

type ParseImpersonateResponseProps = Response;

type GetRedirectUrlProps = ImpersonateData;

export class CustomerService extends BaseService {
  constructor(httpService: HttpService, configService: ConfigService) {
    super(httpService, configService);
  }

  // ============================================================================
  // PUBLIC METHODS - Core Customer API (via HttpService/Axios)
  // ============================================================================

  public async findMany(
    path: string | undefined = undefined,
    search: Record<string, unknown>,
  ) {
    return await this.getMany<ICustomerEntity>(path, search, 'customers');
  }

  public async find(path: string) {
    return this.get<ICustomerEntity>(path, 'customer');
  }

  public async insuranceOverview(path: string) {
    return this.get<IAttachmentEntity>(path, 'customer');
  }

  public async updateIban(props: UpdateIbanProps) {
    const { customerGuid, body } = props;
    return await this.put<void, IbanUpdateDto>(
      `customers/${customerGuid}/iban`,
      new IbanUpdateDto(body),
      'update iban',
    );
  }

  public async updateBillingCycle(props: UpdateBillingCycleProps) {
    const { customerGuid, body } = props;
    return await this.put<void, BillingCycleUpdateDto>(
      `customers/${customerGuid}/billing`,
      new BillingCycleUpdateDto(body),
      'update billing cycle',
    );
  }

  public async updateAddress(props: UpdateAddressProps) {
    const { customerGuid, body } = props;
    const parsedBody = {
      ...new AddressPostDto(body.address),
      effective_date: body.effectiveDate,
    };
    return await this.put<void, IAddressEntity & { effectiveDate: string }>(
      `customers/${customerGuid}/address`,
      parsedBody,
      'update address',
    );
  }

  public async updatePersonalData(props: UpdatePersonalDataParams) {
    const { customerGuid, body } = props;
    const parsedBody = {
      initials: body.initials,
      first_name: body.firstname,
      last_name: body.lastname,
    };
    return await this.post<void, UpdatePersonalDataPayload>(
      `customers/${customerGuid}/update-name`,
      parsedBody,
      'update personal data',
    );
  }

  // ============================================================================
  // PUBLIC METHODS - Auth/Maia API (Raw fetch with token management)
  // ============================================================================

  public async create(body: CustomerCreateDto) {
    const { accessToken, refreshToken } = this.getTokens();

    return this.authFetch({
      url: this.confgService.get('authUserCreate'),
      method: 'POST',
      body: { ...body, refreshToken },
      accessToken,
    }) as Promise<{
      response: {
        token?: {
          accessToken: string;
          refreshToken: string;
          expiresAt: number;
          refreshTokenExpiresAt: number;
        };
        contact_guid: string;
      };
      error: null;
      success: true;
    }>;
  }

  public async remove(props: RemoveProps) {
    const { guid, email } = props;
    const { accessToken, refreshToken } = this.getTokens();

    return this.authFetch({
      url: this.confgService.get('authUserUpdate')(guid),
      method: 'DELETE',
      body: { email, refreshToken },
      accessToken,
    }) as Promise<{
      response: {
        token?: {
          accessToken: string;
          refreshToken: string;
          expiresAt: number;
          refreshTokenExpiresAt: number;
        };
      };
      error: null;
      success: true;
    }>;
  }

  public async updateDetails(props: UpdateDetailsProps) {
    const { guid, old_email, email, phone } = props;
    const { accessToken, refreshToken } = this.getTokens();

    return this.authFetch({
      url: this.confgService.get('authUserUpdate')(guid),
      method: 'PUT',
      body: { old_email, email, phone, refreshToken },
      accessToken,
    }) as Promise<{
      response: {
        token?: {
          accessToken: string;
          refreshToken: string;
          expiresAt: number;
          refreshTokenExpiresAt: number;
        };
      };
      error: null;
      success: true;
    }>;
  }

  // ============================================================================
  // IMPERSONATION - Customer impersonation flow
  // ============================================================================

  public async impersonate(email: string): Promise<ImpersonateResult> {
    const { accessToken } = this.getTokens();
    const url = this.confgService.get('impersonateCustomer')(email);

    try {
      const response = await this.fetchWithRetry({ url, accessToken });
      return this.parseImpersonateResponse(response);
    } catch (error) {
      return {
        type: 'error',
        error: error instanceof Error ? error.message : 'IMPERSONATION_FAILED',
      };
    }
  }

  // ============================================================================
  // PRIVATE HELPERS - Token & Fetch Management
  // ============================================================================

  private getTokens = (): { accessToken: string; refreshToken: string } => {
    const cookies = Cookies.get('auth_tokens');
    if (!cookies) throw new Error('Invalid request');

    const { accessToken, refreshToken } = JSON.parse(cookies) as {
      accessToken?: string;
      refreshToken?: string;
    };

    if (!accessToken || !refreshToken) {
      throw new Error('Invalid request');
    }

    return { accessToken, refreshToken };
  };

  private authFetch = async <T>(props: AuthFetchProps): Promise<T> => {
    const { url, method = 'GET', body, accessToken } = props;
    const response = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      ...(body && { body: JSON.stringify(body) }),
    });

    if (!response.ok) {
      throw new Error('Invalid request');
    }

    return response.json();
  };

  private fetchWithRetry = async (
    props: FetchWithRetryProps,
  ): Promise<Response> => {
    const { url, accessToken } = props;
    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    // Handle 401 by refreshing token and retrying once
    if (response.status === 401) {
      const { refreshToken } = this.getTokens();
      const newTokens = await this.refreshAccessToken(refreshToken);

      const retryResponse = await fetch(url, {
        headers: {
          Authorization: `Bearer ${newTokens.accessToken}`,
        },
      });

      if (!retryResponse.ok) {
        throw new Error('SESSION_EXPIRED');
      }
      return retryResponse;
    }

    return response;
  };

  private refreshAccessToken = async (
    refreshToken: string,
  ): Promise<{ accessToken: string; refreshToken: string }> => {
    const response = await fetch(this.confgService.get('authRefreshToken'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) {
      throw new Error('TOKEN_REFRESH_FAILED');
    }

    const data = (await response.json()) as {
      tokenRes?: {
        accessToken?: string;
        refreshToken?: string;
      };
    };

    const tokens = data.tokenRes;
    if (!tokens?.accessToken || !tokens?.refreshToken) {
      throw new Error('INVALID_REFRESH_RESPONSE');
    }

    // Update stored tokens
    Cookies.set(
      'auth_tokens',
      JSON.stringify({
        accessToken: tokens.accessToken,
        refreshToken: tokens.refreshToken,
      }),
    );

    return {
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
    };
  };

  private parseImpersonateResponse = async (
    props: ParseImpersonateResponseProps,
  ): Promise<ImpersonateResult> => {
    const response = props;
    const payload = (await response
      .json()
      .catch(() => null)) as ImpersonateResponse | null;

    if (!response.ok || !payload?.success) {
      return {
        type: 'error',
        error: payload?.error ?? `IMPERSONATION_FAILED_${response.status}`,
      };
    }

    const redirectUrl = this.getRedirectUrl(payload.response);

    if (!redirectUrl) {
      return { type: 'error', error: 'NO_REDIRECT_URL' };
    }

    return { type: 'ok', data: redirectUrl };
  };

  private getRedirectUrl = (props: GetRedirectUrlProps): string | null => {
    const data = props;
    if (typeof data === 'string') return data;
    return data.url ?? data.data ?? null;
  };
}
